import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  ListItem,
  ListItemText,
  Stack,
  Typography
} from "@material-ui/core";
import {
  Add as AddIcon,
  AssignmentOutlined as QuestionnaireIcon,
  HistoryEdu as HistoryEduIcon
} from "@material-ui/icons";
import {
  displayDocumentStatus,
  getCachedSignatureDefaultTemplates,
  getSignatureDefaultTemplates,
  listDocuments,
  type SignatureDocumentDisplayStatus
} from "services/signatures";
import { buildNewSignatureParams } from "components/_dashboard/signatures/GlobalNewSignatureDialog";
import DocumentDetailPanel from "components/_dashboard/signatures/DocumentDetailPanel";
import QuestionnaireStepLabel from "components/_dashboard/signatures/QuestionnaireStepLabel";
import { signatureDocumentDisplayName } from "utils/signatureQuestionnaireName";
import { signingSetOf } from "utils/signatureQuestionnaireStep";
import { SIGNATURE_QUESTIONNAIRE_ONLY_TEMPLATE_KEY } from "../../../../../@types/sharedSchema";
import type { SignatureDocument } from "../../../../../@types/signatures";

const statusColor = (status: SignatureDocumentDisplayStatus) => {
  switch (status) {
    case "completed":
      return "success" as const;
    case "declined":
    case "expired":
      return "error" as const;
    case "partial":
      return "warning" as const;
    case "sent":
      return "info" as const;
    default:
      return "default" as const;
  }
};

type Props = {
  listingId: string;
  /** The property is offered for rent: its owner is a landlord (שאלון בעל זכות בנכס), else a seller. */
  isRentListing?: boolean;
};

/** "שליחת שאלון לבעל הנכס" works only when the questionnaire-only slot has a template (the user's or Webot's). */
const questionnaireSlotSet = (): boolean =>
  !!getCachedSignatureDefaultTemplates()?.effective?.[SIGNATURE_QUESTIONNAIRE_ONLY_TEMPLATE_KEY];

export function ListingTableSignatures({ listingId, isRentListing = false }: Props): JSX.Element {
  const { t: translate } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [canSendQuestionnaire, setCanSendQuestionnaire] = useState<boolean>(questionnaireSlotSet);

  useEffect(() => {
    if (getCachedSignatureDefaultTemplates()) return undefined;
    let cancelled = false;
    getSignatureDefaultTemplates()
      .then(() => {
        if (!cancelled) setCanSendQuestionnaire(questionnaireSlotSet());
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const [documents, setDocuments] = useState<SignatureDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDocumentId, setOpenDocumentId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const result = await listDocuments({ listingId });
        if (cancelled) return;
        result.sort((a, b) =>
          (b.sentAt || b.createdTime || "").localeCompare(a.sentAt || a.createdTime || "")
        );
        setDocuments(result);
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.response?.data?.error || err?.message || "Failed to load signatures");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [listingId]);

  const openDocument = (id: string): void => {
    setOpenDocumentId(id);
  };

  const closeDocument = (): void => {
    setOpenDocumentId(null);
  };

  const openNewSignatureDialog = (): void => {
    const next = new URLSearchParams(searchParams);
    // Pre-select THIS listing in the send form — a contract opened from a
    // listing's own signatures tab is about that listing, and without the
    // link the sent document never files back under this tab.
    Object.entries(buildNewSignatureParams({ listingIds: [listingId] })).forEach(([k, v]) =>
      next.set(k, v)
    );
    setSearchParams(next);
  };

  // The owner's questionnaire on its own (שאלון בלבד): the send form opens on the owner's role — a
  // landlord for a rental, else a seller — with "שאלון בלבד" ticked and THIS property picked.
  const openQuestionnaireDialog = (): void => {
    const next = new URLSearchParams(searchParams);
    Object.entries(
      buildNewSignatureParams({
        listingIds: [listingId],
        role: isRentListing ? "landlord" : "seller",
        questionnaireOnly: true
      })
    ).forEach(([k, v]) => next.set(k, v));
    setSearchParams(next);
  };

  const questionnaireButton = (variant: "outlined" | "text"): JSX.Element => (
    <Button variant={variant} startIcon={<QuestionnaireIcon />} onClick={openQuestionnaireDialog}>
      {translate("listingsPage.signatures.sendQuestionnaire")}
    </Button>
  );

  let body: JSX.Element;
  if (loading) {
    body = <LinearProgress />;
  } else if (error) {
    body = (
      <Typography variant="body2" color="error">
        {error}
      </Typography>
    );
  } else if (!documents.length) {
    body = (
      <Stack spacing={2} alignItems="center" sx={{ textAlign: "center", py: 6, px: 2 }}>
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            bgcolor: "primary.lighter",
            color: "primary.main",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <HistoryEduIcon sx={{ fontSize: 36 }} />
        </Box>
        <Stack spacing={0.5} alignItems="center">
          <Typography variant="h6">
            {translate("listingsPage.signatures.empty.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate("listingsPage.signatures.empty.subtitle")}
          </Typography>
        </Stack>
        <Button
          variant="contained"
          size="large"
          startIcon={<AddIcon />}
          onClick={openNewSignatureDialog}
        >
          {translate("signatures.newDocument")}
        </Button>
        {canSendQuestionnaire && questionnaireButton("outlined")}
      </Stack>
    );
  } else {
    body = (
      <Stack divider={<Divider />} spacing={0}>
        {canSendQuestionnaire && (
          <Stack direction="row" justifyContent="flex-end" sx={{ pb: 1 }}>
            {questionnaireButton("text")}
          </Stack>
        )}
        {documents.map((doc) => {
          const sentAt = doc.sentAt ? new Date(doc.sentAt).toLocaleDateString("he-IL") : null;
          return (
            <ListItem
              key={doc.id}
              onClick={() => openDocument(doc.id)}
              sx={{
                cursor: "pointer",
                "&:hover": { bgcolor: "action.hover" }
              }}
            >
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="subtitle2" component="span">
                      {doc.formattedNumber}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" component="span">
                      · {signatureDocumentDisplayName(doc)}
                    </Typography>
                  </Stack>
                }
                secondary={
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: "wrap", rowGap: 0.5 }}>
                    <Chip
                      label={translate(`signatures.status.${displayDocumentStatus(doc)}`)}
                      color={statusColor(displayDocumentStatus(doc)) as any}
                      size="small"
                    />
                    {/* The order's questionnaire step (the lists' one rule): "+ שאלון מוכר", then
                        "נחתם, ממתין לשאלון" once the owner signed and the questionnaire is open. */}
                    <QuestionnaireStepLabel doc={doc} />
                    {sentAt && (
                      <Typography variant="caption" color="text.secondary">
                        {sentAt}
                      </Typography>
                    )}
                  </Stack>
                }
                secondaryTypographyProps={{ component: "div" }}
              />
            </ListItem>
          );
        })}
      </Stack>
    );
  }

  return (
    <>
      {body}
      {openDocumentId && (
        <>
          <Box
            onClick={closeDocument}
            sx={{
              position: "fixed",
              inset: 0,
              bgcolor: "rgba(0,0,0,0.45)",
              zIndex: 2000
            }}
          />
          <Box
            role="dialog"
            aria-modal="true"
            sx={{
              position: "fixed",
              top: 0,
              bottom: 0,
              insetInlineEnd: 0,
              width: { xs: "100%", md: 480 },
              maxWidth: "100%",
              bgcolor: "background.paper",
              boxShadow: 6,
              zIndex: 2001,
              overflow: "auto",
              display: "flex",
              flexDirection: "column"
            }}
          >
            <DocumentDetailPanel
              documentId={openDocumentId}
              onClose={closeDocument}
              setDocuments={(() => {
                const open = documents.find((doc) => doc.id === openDocumentId);
                return open ? signingSetOf(open, documents) : undefined;
              })()}
            />
          </Box>
        </>
      )}
    </>
  );
}
